import { DomHelper, DragHelper, Tooltip } from "@bryntum/scheduler";
import blockTooltipRenderer from "components/common/Scheduler/SchedulerRenderers/renderers/blockTooltipRenderer";
import overlappingBreaksIconRenderer from "components/common/Scheduler/SchedulerRenderers/renderers/overlappingBreaksIconRenderer";
import renderToString from "components/common/Scheduler/SchedulerRenderers/renderers/renderToString";
import Schedule from "domain/Schedule";
import WorkBlock from "domain/WorkBlock";
import EventTypes from "hooks/apollo/usageData/EventTypes";
import DateTime from "utils/DateTime";
import BLOCK_HEIGHT from "./blockHeight";
import GapFiller from "./GapFiller";
import LineHighlighter from "./LineHighlighter";
import OverlappingPlannedBreaks from "./OverlappingPlannedBreaks";

export default class DragFromSidebar extends DragHelper {
  static get defaultConfig() {
    return {
      cloneTarget: true,
      dropTargetSelector: ".b-timeline-subgrid",
      mode: "translateXY",
      targetSelector: ".draggable",
    };
  }

  construct(config) {
    super.construct(config);

    this.on({
      drag: this.onTaskDrag,
      dragstart: this.onTaskDragStart,
      drop: this.onTaskDrop,
      thisObj: this,
    });

    this.settings = this.applicationContext.settings;
    this.onDrop = config.onDrop;
    this.draggedNodeRenderer = config.draggedNodeRenderer;
    this.lineHighlighter = new LineHighlighter();
    this.gapFiller = new GapFiller({ settings: this.settings });
    this.gapFiller.setSchedule(this.schedule);
    this.onTrackUsage = config.onTrackUsage;
  }

  onTaskDragStart({ context }) {
    const { element } = context;

    context.eventName = context.grabbed.innerText;
    this.tooltip = new Tooltip({
      align: "b-t",
      forElement: element,
    });

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    element.style.height = `${BLOCK_HEIGHT}px`;
    element.innerHTML = this.draggedNodeRenderer(element.dataset);
    this.enableScrolling();
  }

  onTaskDrag({ context, event }) {
    this.bryntumEvent = event;
    const { element } = context;
    const line = this.schedule.resolveResourceRecord(context.target);

    if (line) {
      this.line = line;
    }

    this.gapFiller.onTaskDrag({
      cursorPositionX: event.clientX,
      line,
    });

    if (this.line) {
      this.lineHighlighter.start(this.line.id);
    }

    this.centerNodeOnMouse({ context });

    if (context.valid) {
      const { startDate, endDate } = this.timeSpanForRemainingDuration(element);

      const workOrder = JSON.parse(element.dataset.dragWorkOrder);

      const fakeWorkBlock = WorkBlock.buildWithBlockTime({
        endsAt: DateTime.toISO8601(endDate),
        line: this.line,
        lockedOnSchedule: false,
        performance: workOrder.performance,
        standardProductionRate: workOrder.standardProductionRate,
        startsAt: DateTime.toISO8601(startDate),
        unitOfMeasure: workOrder.unitOfMeasure,
        workOrder,
      });

      const duration = WorkBlock.workTimeDuration(fakeWorkBlock);
      const width = this.schedule.timeAxisViewModel.getDistanceForDuration(duration * 1000);

      element.style.width = `${width}px`;

      if (fakeWorkBlock.overlappingPlannedBreaks.length > 0) {
        element.getElementsByClassName("overlapping-breaks")[0].setHTMLUnsafe(overlappingBreaksIconRenderer());
      } else {
        element.getElementsByClassName("overlapping-breaks")[0].setHTMLUnsafe("");
      }

      fakeWorkBlock.remainingDuration = Schedule.remainingDurationForBrandNewWorkBlock(fakeWorkBlock);

      this.tooltip.html = renderToString(
        blockTooltipRenderer({
          applicationContext: this.applicationContext,
          block: fakeWorkBlock,
          displaySnappedRemainingDuration: false,
          endDate,
          gapFiller: this.gapFiller,
          line: this.line,
          showStatus: false,
          startDate,
        }),
      );
      this.tooltip.showBy({ target: element });
      this.tooltip.realign();
    } else {
      this.tooltip.hide();
    }
  }

  // Drop callback after a mouse up, take action and transfer the unplanned task to the real EventStore (if it's valid)
  onTaskDrop({ context }) {
    const { valid, element } = context;

    this.tooltip.hide();
    this.lineHighlighter.stop();
    this.updateUIOnDrop();

    if (!valid || !this.line || this.gapFiller.shouldAbortFillInGap()) {
      this.abortDrop();

      return;
    }
    const { startDate, endDate } = this.timeSpanForRemainingDuration(element);

    // `context.finalize()` needs to run before onDrop (or any mutation) to avoid bugs.
    context.finalize();

    this.onDrop({
      endDate,
      line: this.line.data,
      startDate,
      workOrder: JSON.parse(element.dataset.dragWorkOrder),
    });

    if (this.gapFiller.isActive()) {
      this.onTrackUsage(EventTypes.FILL_IN_GAP);
    }

    this.clearDragState();
  }

  timeSpanForRemainingDuration(element) {
    const fullShiftTimespan = this.gapFiller.timespanWithinGap();
    let endDate;
    const startDate = fullShiftTimespan ? fullShiftTimespan.startTime : this.getStartsAt();

    const { totalDuration } = this.getBlockDurationInSeconds({ ...element.dataset, startsAt: startDate });

    if (fullShiftTimespan) {
      const timespan = Schedule.shrinkTimeSpanToWorkOrderRemaining({
        remainingBlockDuration: totalDuration,
        timespan: fullShiftTimespan,
      });

      endDate = timespan.endTime;
    } else {
      endDate = DateTime.addDurationDate(startDate, totalDuration, "seconds");
    }

    return { endDate, startDate };
  }

  getBlockDurationInSeconds(dataset) {
    if (!this.line) {
      return Number(dataset.dragRemainingDuration);
    }

    const workBlock = this.buildBlock(dataset);

    const requiredDuration = WorkBlock.calculateBlockDurationForLine(workBlock, this.line);
    const overlappingPlannedBreaks = this.getOverlappingPlannedBreaks(workBlock, requiredDuration);
    const overlappingPlannedBreakDuration = overlappingPlannedBreaks.reduce(
      (acc, breakDuration) => acc + breakDuration.duration,
      0,
    );

    return { overlappingPlannedBreaks, totalDuration: requiredDuration + overlappingPlannedBreakDuration };
  }

  getOverlappingPlannedBreaks(workBlock, requiredDuration) {
    const overlappingPlannedBreaks = new OverlappingPlannedBreaks({
      line: this.line,
    });

    return overlappingPlannedBreaks.getOverlappingPlannedBreaks(workBlock, requiredDuration * 1000);
  }

  buildBlock(dataset) {
    return {
      performance: dataset.dragPerformance,
      quantity: dataset.dragUnscheduledQuantity,
      standardProductionRate: dataset.dragStandardProductionRate,
      startsAt: DateTime.toISO8601(dataset.startsAt),
      teardownTimeDuration: 0,
    };
  }

  abortDrop() {
    this.abort();
    this.clearDragState();
  }

  clearDragState() {
    this.disableScrolling();
    this.line = null;
    this.gapFiller.clearDragState();
  }

  updateUIOnDrop() {
    document.body.style.userSelect = "auto";
    document.body.style.cursor = "auto";
  }

  enableScrolling() {
    const {
      schedule: { scrollManager, timeAxisSubGridElement },
    } = this.config;

    scrollManager.startMonitoring(timeAxisSubGridElement);
  }

  disableScrolling() {
    const {
      schedule: { scrollManager, timeAxisSubGridElement },
    } = this.config;

    scrollManager.stopMonitoring(timeAxisSubGridElement);
  }

  centerNodeOnMouse({ context }) {
    const { pageY: mouseY, pageX: mouseX, element } = context;

    DomHelper.setTranslateY(element, mouseY - BLOCK_HEIGHT / 2);
    DomHelper.setTranslateX(element, mouseX - 20);
  }

  getStartsAt() {
    return DateTime.momentDate(this.schedule.getDateFromCoordinate(this.bryntumEvent.clientX - 20, "round", false));
  }
}
